import { supabase } from '../lib/supabase';

const SUPABASE_FUNCTION_URL = import.meta.env?.VITE_SUPABASE_URL?.replace(
  'https://',
  'https://'
)?.replace('.supabase.co', '.supabase.co/functions/v1');

/**
 * Service for sending social notification emails via Resend
 */
export const socialEmailService = {
  /**
   * Send duel invitation email
   */
  async sendDuelInviteEmail(opponentId, duelData) {
    try {
      // Get opponent profile
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, marketing_emails')
        ?.eq('id', opponentId)
        ?.single();

      if (profileError) throw profileError;
      if (!profile?.marketing_emails) {
        console.log('User has not opted in for marketing emails');
        return { success: false, reason: 'not_opted_in' };
      }

      // Get challenger name
      const { data: challenger } = await supabase
        ?.from('user_profiles')
        ?.select('full_name')
        ?.eq('id', duelData?.creatorId)
        ?.single();

      // Send email via Edge Function
      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-social-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'duel_invite',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          notificationData: {
            challengerName: challenger?.full_name || 'Someone',
            duelTitle: duelData?.title,
            duelDescription: duelData?.description,
            metric: duelData?.metric,
            targetValue: duelData?.targetValue,
            duration: duelData?.durationDays,
            stake: duelData?.stakeName || 'Bragging rights',
            appUrl: `${window?.location?.origin}/social-hub`
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');

      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending duel invite email:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send friend request accepted email
   */
  async sendFriendAcceptedEmail(userId, acceptorId) {
    try {
      // Get user profile (person who sent the request)
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, marketing_emails')
        ?.eq('id', userId)
        ?.single();

      if (profileError) throw profileError;
      if (!profile?.marketing_emails) {
        console.log('User has not opted in for marketing emails');
        return { success: false, reason: 'not_opted_in' };
      }

      // Get acceptor name
      const { data: acceptor } = await supabase
        ?.from('user_profiles')
        ?.select('full_name, avatar_url')
        ?.eq('id', acceptorId)
        ?.single();

      // Send email via Edge Function
      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-social-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'friend_request_accepted',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          notificationData: {
            senderName: acceptor?.full_name || 'Someone',
            senderAvatar: acceptor?.avatar_url,
            appUrl: `${window?.location?.origin}/social-hub`
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');

      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending friend accepted email:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send photo proof received email
   */
  async sendPhotoProofEmail(duelId, uploaderId, recipientId) {
    try {
      // Get recipient profile
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, marketing_emails')
        ?.eq('id', recipientId)
        ?.single();

      if (profileError) throw profileError;
      if (!profile?.marketing_emails) {
        console.log('User has not opted in for marketing emails');
        return { success: false, reason: 'not_opted_in' };
      }

      // Get uploader name
      const { data: uploader } = await supabase
        ?.from('user_profiles')
        ?.select('full_name')
        ?.eq('id', uploaderId)
        ?.single();

      // Get duel details
      const { data: duel } = await supabase
        ?.from('duels')
        ?.select('title')
        ?.eq('id', duelId)
        ?.single();

      // Send email via Edge Function
      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-social-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'photo_proof_received',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          notificationData: {
            uploaderName: uploader?.full_name || 'Your opponent',
            duelTitle: duel?.title || 'Your Duel',
            appUrl: `${window?.location?.origin}/social-hub`
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');

      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending photo proof email:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send achievement milestone email
   */
  async sendAchievementMilestoneEmail(userId, achievementData) {
    try {
      // Get user profile
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, marketing_emails')
        ?.eq('id', userId)
        ?.single();

      if (profileError) throw profileError;
      if (!profile?.marketing_emails) {
        console.log('User has not opted in for marketing emails');
        return { success: false, reason: 'not_opted_in' };
      }

      // Send email via Edge Function
      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-social-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'achievement_milestone',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          notificationData: {
            title: achievementData?.title,
            description: achievementData?.description,
            icon: achievementData?.icon || '🏆',
            pointsEarned: achievementData?.pointsEarned || 0,
            category: achievementData?.category || 'achievement',
            appUrl: `${window?.location?.origin}/social-hub`
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');

      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending achievement milestone email:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send activity streak update email
   */
  async sendActivityStreakEmail(userId, streakData) {
    try {
      // Get user profile
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, marketing_emails')
        ?.eq('id', userId)
        ?.single();

      if (profileError) throw profileError;
      if (!profile?.marketing_emails) {
        console.log('User has not opted in for marketing emails');
        return { success: false, reason: 'not_opted_in' };
      }

      // Send email via Edge Function
      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-social-notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'activity_streak',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          notificationData: {
            streakDays: streakData?.streakDays,
            metric: streakData?.metric || 'activity',
            streakMessage: streakData?.streakMessage || `You've maintained a ${streakData?.streakDays}-day streak!`,
            bestStreak: streakData?.bestStreak || streakData?.streakDays,
            appUrl: `${window?.location?.origin}/dashboard`
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');

      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending activity streak email:', error);
      return { success: false, error: error?.message };
    }
  }
};

export default socialEmailService;

/**
 * Service for sending email summaries for badges, milestones, duels, and friend activity
 */
export const emailSummaryService = {
  /**
   * Send badge earned email summary (checks email_badges preference)
   */
  async sendBadgeEmail(userId, badgeData) {
    try {
      const { data: profile, error } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, email_badges')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;
      if (!profile?.email_badges) return { success: false, reason: 'not_opted_in' };

      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-email-summaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'badge_earned',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          summaryData: {
            badgeName: badgeData?.name || badgeData?.title,
            badgeIcon: badgeData?.icon || '🏅',
            badgeDescription: badgeData?.description || '',
            pointsEarned: badgeData?.points || 0,
            appUrl: window?.location?.origin
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');
      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending badge email summary:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send milestone reached email summary (checks email_milestones preference)
   */
  async sendMilestoneEmail(userId, milestoneData) {
    try {
      const { data: profile, error } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, email_milestones')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;
      if (!profile?.email_milestones) return { success: false, reason: 'not_opted_in' };

      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-email-summaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'milestone_reached',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          summaryData: {
            milestoneTitle: milestoneData?.title,
            milestoneDescription: milestoneData?.description || '',
            milestoneIcon: milestoneData?.icon || '🎯',
            pointsEarned: milestoneData?.points || 0,
            category: milestoneData?.category || 'fitness',
            appUrl: window?.location?.origin
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');
      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending milestone email summary:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send duel event email summary (checks email_duels preference)
   */
  async sendDuelSummaryEmail(userId, duelData) {
    try {
      const { data: profile, error } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, email_duels')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;
      if (!profile?.email_duels) return { success: false, reason: 'not_opted_in' };

      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-email-summaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'duel_summary',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          summaryData: {
            duelTitle: duelData?.title,
            eventType: duelData?.eventType || 'invite',
            opponentName: duelData?.opponentName || 'Your opponent',
            isWinner: duelData?.isWinner || false,
            metric: duelData?.metric || '',
            appUrl: window?.location?.origin
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');
      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending duel summary email:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send friend activity email summary (checks email_friend_activity preference)
   */
  async sendFriendActivityEmail(userId, activityData) {
    try {
      const { data: profile, error } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name, email_friend_activity')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;
      if (!profile?.email_friend_activity) return { success: false, reason: 'not_opted_in' };

      const response = await fetch(`${SUPABASE_FUNCTION_URL}/send-email-summaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${(await supabase?.auth?.getSession())?.data?.session?.access_token}`
        },
        body: JSON.stringify({
          emailType: 'friend_activity',
          recipientEmail: profile?.email,
          recipientName: profile?.full_name,
          summaryData: {
            friendName: activityData?.friendName,
            activityType: activityData?.activityType || 'workout',
            activityTitle: activityData?.title || 'New Activity',
            activityDescription: activityData?.description || '',
            activityIcon: activityData?.icon || '💪',
            appUrl: window?.location?.origin
          }
        })
      });

      const result = await response?.json();
      if (!response?.ok) throw new Error(result?.error || 'Failed to send email');
      return { success: true, messageId: result?.messageId };
    } catch (error) {
      console.error('Error sending friend activity email summary:', error);
      return { success: false, error: error?.message };
    }
  }
};